import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, http } from '@/api/client'
import type { StoredFile } from '@/features/students/types'
import type { ApiItem } from '@/types/api'
import type {
  AssignmentPayload,
  CourseCard,
  FamilyCourse,
  FamilyOverview,
  MaterialPayload,
  SubmissionRoster,
  TeacherCourse,
  VideoState,
} from './types'

export const lmsKeys = {
  all: ['lms'] as const,
  courses: (classId?: number) => [...lmsKeys.all, 'courses', classId ?? 'mine'] as const,
  course: (sectionId: number, subjectId: number) => [...lmsKeys.all, 'course', sectionId, subjectId] as const,
  submissions: (assignmentId: number) => [...lmsKeys.all, 'submissions', assignmentId] as const,
  family: (studentId: number) => [...lmsKeys.all, 'family', studentId] as const,
  familyCourse: (studentId: number, subjectId: number) =>
    [...lmsKeys.all, 'family', studentId, subjectId] as const,
  video: (id: number) => [...lmsKeys.all, 'video', id] as const,
}

/** Polls a video while the server converts it. */
export const videoQuery = (id: number) =>
  queryOptions({
    queryKey: lmsKeys.video(id),
    queryFn: () => api.get<VideoState>(`/lms/videos/${id}`),
    refetchInterval: (q) =>
      q.state.data && ['ready', 'failed'].includes(q.state.data.status) ? false : 4000,
  })

export const coursesQuery = (classId?: number) =>
  queryOptions({
    queryKey: lmsKeys.courses(classId),
    queryFn: () => api.get<CourseCard[]>('/lms/courses', { params: classId ? { classId } : undefined }),
  })
export const courseQuery = (sectionId: number, subjectId: number) =>
  queryOptions({
    queryKey: lmsKeys.course(sectionId, subjectId),
    queryFn: () => api.get<TeacherCourse>(`/lms/courses/${sectionId}/${subjectId}`),
  })
export const submissionsQuery = (assignmentId: number) =>
  queryOptions({
    queryKey: lmsKeys.submissions(assignmentId),
    queryFn: () => api.get<SubmissionRoster>(`/lms/assignments/${assignmentId}/submissions`),
  })
export const familyLmsQuery = (studentId: number) =>
  queryOptions({
    queryKey: lmsKeys.family(studentId),
    queryFn: () => api.get<FamilyOverview>(`/me/family/students/${studentId}/lms`),
  })
export const familyCourseQuery = (studentId: number, subjectId: number) =>
  queryOptions({
    queryKey: lmsKeys.familyCourse(studentId, subjectId),
    queryFn: () => api.get<FamilyCourse>(`/me/family/students/${studentId}/lms/courses/${subjectId}`),
  })

function useLmsMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: lmsKeys.all }),
  })
}

export const useSaveMaterial = (sectionId: number, subjectId: number) =>
  useLmsMutation(({ id, ...body }: MaterialPayload & { id?: number }) =>
    id
      ? api.put<TeacherCourse>(`/lms/materials/${id}`, body)
      : api.post<TeacherCourse>(`/lms/courses/${sectionId}/${subjectId}/materials`, body),
  )
export const useDeleteMaterial = () => useLmsMutation((id: number) => api.delete(`/lms/materials/${id}`))
export const useSaveAssignment = (sectionId: number, subjectId: number) =>
  useLmsMutation(({ id, ...body }: AssignmentPayload & { id?: number }) =>
    id
      ? api.put<TeacherCourse>(`/lms/assignments/${id}`, body)
      : api.post<TeacherCourse>(`/lms/courses/${sectionId}/${subjectId}/assignments`, body),
  )
export const useDeleteAssignment = () => useLmsMutation((id: number) => api.delete(`/lms/assignments/${id}`))
export const useGrade = () =>
  useLmsMutation(({ id, points, feedback }: { id: number; points: number | null; feedback: string | null }) =>
    api.put<SubmissionRoster>(`/lms/submissions/${id}/grade`, { points, feedback }),
  )
export const useSubmit = (studentId: number) =>
  useLmsMutation(
    ({ assignmentId, body, fileIds }: { assignmentId: number; body: string | null; fileIds: number[] }) =>
      api.put<FamilyCourse>(`/me/family/students/${studentId}/lms/assignments/${assignmentId}/submission`, {
        body,
        fileIds,
      }),
  )

export function useUploadLmsFile() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return (await http.post<ApiItem<StoredFile>>('/lms/files', form)).data.data
    },
  })
}
